import React, { useState, useEffect } from 'react';
import axios from 'axios.js';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { TextField, MenuItem } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { useToastify } from 'app/hooks/useToastify';
import { Button, Input } from 'app/components';
import { AxiosResponse } from 'axios';

interface User {
  id: string;
  name: string;
  cpf: string;
  email: string;
  celular: string;
  role: string;
  type: string;
  perfilId: string;
}

// Esquema de validação usando Yup
const validationSchema = Yup.object({
  name: Yup.string()
    .required('O nome completo é obrigatório'),
  cpf: Yup.string()
    .min(11, "Informe um CPF válido")
    .required('O CPF é obrigatório'),
  email: Yup.string()
    .email('Email inválido')
    .required('O email é obrigatório'),
  perfilId: Yup.string()
    .required('Selecione um perfil'),
  celular: Yup.string()
    .max(11)
    .optional(),
  setor: Yup.string()
    .optional(),
});

type ValidationSchemaType = Yup.InferType<typeof validationSchema>;

const defaultInitialValues = {
  name: "",
  cpf: "",
  email: "",
  celular: "",
  setor: "",
  perfilId: "",
};

interface UserFormProps { 
  userId: string | null
}

function UserForm({ userId }: UserFormProps) {
  const history = useHistory()
  const toast = useToastify();

  const [roles, setRoles] = useState([]);
  const [user, setUser] = useState<User>();

  const [initialValuesForm, setInitalValuesForm] = useState<ValidationSchemaType>(defaultInitialValues);

  useEffect(() => {
    async function fetchRoles() {
      const response = await axios.get('/perfil?active=true');
      setRoles(response.data.perfil);
    }
    fetchRoles();
  }, []);

  useEffect(() => {
    if (userId) {
      getUser(userId);
    }
  }, [userId]);

  async function getUser(userId: string) {
    try {
      const response = await axios.get(`/users/admin/${userId}`);
      if (response.status === 200) {
        const userData = response.data.user;
        
        setInitalValuesForm({ 
          name: userData?.name ?? '', 
          cpf: userData?.cpf ?? '', 
          email: userData?.email ?? '',  
          perfilId: userData?.perfilId ?? '',  
          celular: userData?.celular ?? '',  
          setor: '', 
        })

        setUser(user)
      }
    } catch (e) {
      console.error(e);
    }
  }

  const handlePressVoltar = () => {
    history.goBack()
  }
  
  const sanitizeValuesSubmit = (values: ValidationSchemaType): ValidationSchemaType => {
    return {
      ...values,
      name: values.name.trim().toUpperCase(),
      cpf: values.cpf.replace(/\D/g, ""),
      email: values.email.trim().toLocaleLowerCase(),
      celular: values.celular?.replace(/\D/g, ""),
    }
  }

  const onSubmit = async (
    values: ValidationSchemaType, 
    { setSubmitting, resetForm }: FormikHelpers<ValidationSchemaType>
  ) => {
    try {

      const sanitizedValues = sanitizeValuesSubmit(values);

      let response: AxiosResponse<any, any>;

      if (userId) {
        response = await axios.patch(`/users/admin/${userId}`, {
          ...sanitizedValues
        });
      } else {
        response = await axios.post('/users/admin', {
          ...sanitizedValues,
          role: 'admin',
        });
      }

      if (![201, 200].includes(response.status)) {
        toast.show(
          'error',
          response.data?.message ?? `Não foi possível ${userId ? 'editar' : 'criar'} o usuário!`
        );
        throw new Error(response.data?.message ?? response.statusText);
      }

      toast.show(
        'success',
        `Usuário  ${userId ? 'editado' : 'criado'} com sucesso!`
      );

      resetForm();
      history.replace('/usuario');
    } catch (error) {
      console.error('Erro ao salvar o usuário:', error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="tw-w-full tw-px-6 tw-pb-6">
      <div className="tw-p-6 tw-bg-white tw-shadow-md tw-rounded-lg">
        <h2 className="tw-text-xl tw-font-bold tw-mb-4">Usuários cadastrados</h2>
        
        {/* Formik para manipulação do formulário */}
        <Formik
          initialValues={initialValuesForm}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, touched, errors, handleChange, setFieldValue }) => (
            <Form className="tw-grid tw-grid-cols-3 tw-gap-8">
              {/* Nome Completo */}
              <Input.Root className="tw-flex tw-flex-col tw-w-full">
                <Input.Label required text="Nome completo" />
                <TextField
                  name="name"
                  placeholder="Informe o nome completo"
                  inputProps={{ maxLength: 100 }}
                  onChange={handleChange}
                  value={values.name}
                  size='small'
                />
                {errors.name && (<Input.Error text={errors.name as string} />)}
              </Input.Root>

              {/* CPF */}
              <Input.Root className="tw-flex tw-flex-col tw-w-full">
                <Input.Label required text="CPF" />
                <TextField
                  name="cpf"
                  placeholder="Informe o CPF"
                  inputProps={{ maxLength: 11 }}
                  onChange={handleChange}
                  value={values.cpf}
                  size='small'
                />
                {errors.cpf && (<Input.Error text={errors.cpf as string} />)}
              </Input.Root>

              {/* Email */}
              <Input.Root className="tw-flex tw-flex-col tw-w-full">
                <Input.Label required text="E-mail" />
                <TextField
                  name="email"
                  placeholder="Informe o e-mail"
                  inputProps={{ maxLength: 100 }}
                  onChange={handleChange}
                  value={values.email}
                  type='email'
                  size='small'
                />
                {errors.email && (<Input.Error text={errors.email as string} />)}
              </Input.Root>

              {/* Telefone */}
              <Input.Root className="tw-flex tw-flex-col tw-w-full">
                <Input.Label text="Telefone" />
                <TextField
                  name="celular"
                  placeholder="Informe o telefone"
                  inputProps={{ maxLength: 11 }}
                  onChange={handleChange}
                  value={values.celular}
                  size='small'
                />
                {errors.celular && (<Input.Error text={errors.celular as string} />)}
              </Input.Root>

              {/* Setor */}
              <Input.Root className="tw-flex tw-flex-col tw-w-full">
                <Input.Label text="Setor" />
                <TextField
                  name="setor"
                  placeholder="Informe o setor"
                  inputProps={{ maxLength: 100 }}
                  onChange={handleChange}
                  value={values.setor}
                  size='small'
                />
                {errors.setor && (<Input.Error text={errors.setor as string} />)}
              </Input.Root>

              {/* Perfil */}
              <Input.Root className="tw-flex tw-flex-col tw-w-full">
                <Input.Label required text="Perfil" />
                <TextField
                  name="perfilId"
                  placeholder="Informe o setor"
                  onChange={handleChange}
                  value={values.perfilId}
                  size='small'
                  select
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </TextField>
                {errors.perfilId && (<Input.Error text={errors.perfilId as string} />)}
              </Input.Root>

              {/* Botões Voltar e Salvar */}
              <div className="tw-col-span-3 tw-flex tw-justify-end tw-space-x-4 tw-mt-4">
                <Button.Element
                  variant='secondary'
                  className='tw-w-full tw-min-w-[180px] tw-max-w-[200px]'
                  onClick={handlePressVoltar}
                >
                  <Button.Text variant='secondary' text='Voltar' /> 
                </Button.Element>
                <Button.Element 
                  type="submit"
                  className='tw-w-full tw-min-w-[180px] tw-max-w-[200px]'
                >
                  <Button.Text text={isSubmitting ? 'Salvando...' : 'Salvar'} /> 
                </Button.Element>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default UserForm;
